
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { Mail, MessageSquare, Copy, Check, Edit, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

interface Message {
  id: string;
  content: string;
  message_type: "email" | "text";
  created_at: string;
  clients?: {
    client_name: string;
  };
}

interface MessageListProps {
  messages: Message[];
  messageType: "email" | "text";
  description?: string;
}

export const MessageList = ({ messages, messageType, description }: MessageListProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  if (!messages || messages.length === 0) return null;

  const handleEdit = (message: Message) => {
    setEditingId(message.id);
    setEditContent(message.content);
  };

  const handleSave = (messageId: string) => {
    setEditingId(null);
    toast({
      title: "Changes saved",
      description: "Your message has been updated successfully.",
    });
  };

  const handleCopy = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      toast({
        title: "Copied to clipboard",
        description: "Message content has been copied successfully.",
      });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Failed to copy message to clipboard.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tighter">
          {messageType === 'email' ? 'Email Message' : 'Text Message'}
        </h2>
        {description && (
          <p className="text-gray-600 text-sm sm:text-base">
            {description}
          </p>
        )}
      </div>
      <Card className="p-3 sm:p-6">
        <div className="space-y-4 sm:space-y-6">
          {messages?.map((message) => (
            <div key={message.id} className="bg-white rounded-lg border shadow-sm transition-all hover:shadow-md">
              <div className="p-3 sm:p-4 border-b">
                <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center justify-between'}`}>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-base sm:text-lg">
                      {message.clients?.client_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Badge 
                        variant={message.message_type === 'email' ? 'default' : 'secondary'}
                        className="flex items-center gap-1"
                      >
                        {message.message_type === 'email' ? (
                          <Mail className="h-3 w-3" />
                        ) : (
                          <MessageSquare className="h-3 w-3" />
                        )}
                        {message.message_type}
                      </Badge>
                      <span className="text-xs sm:text-sm text-gray-500">
                        {format(new Date(message.created_at), isMobile ? 'PP' : 'PPp')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(message.content, message.id)}
                      className="flex items-center gap-1"
                    >
                      {copiedId === message.id ? (
                        <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                      ) : (
                        <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                      <span className="text-xs sm:text-sm">
                        {copiedId === message.id ? "Copied!" : "Copy"}
                      </span>
                    </Button>
                    {editingId !== message.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(message)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm">Edit</span>
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingId(null)}
                        className="flex items-center gap-1"
                      >
                        <X className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm">Cancel</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-3 sm:p-4 bg-gray-50 rounded-b-lg">
                <div className={`bg-white p-3 sm:p-4 rounded-lg border ${!editingId && 'max-h-[300px] overflow-y-auto'}`}>
                  {editingId === message.id ? (
                    <div className="space-y-4">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="min-h-[150px] leading-relaxed text-sm sm:text-base"
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingId(null)}
                          className="text-xs sm:text-sm"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSave(message.id)}
                          className="text-xs sm:text-sm"
                        >
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-600 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">
                      {message.content}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
